/**
 * Tasks context — reads the `tasks` field from LangGraph thread state
 * and exposes it for the TaskReview component and thread listing.
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

// ---------------------------------------------------------------------------
// Types (mirrors the backend Task schema)
// ---------------------------------------------------------------------------

export interface Task {
  id: string;
  tool_name: string;
  tool_args: Record<string, unknown>;
  title: string;
  description?: string;
  reasoning?: string;
  impact?: string;
  url?: string;
  depends_on?: string[];
  status: 'pending' | 'approved' | 'rejected' | 'completed' | 'failed';
  result?: string;
  editable_fields?: string[];
}

// ---------------------------------------------------------------------------
// Extract from state values
// ---------------------------------------------------------------------------

/**
 * Extract tasks from LangGraph thread state values.
 * Returns an empty array if no tasks exist or the shape doesn't match.
 */
export function extractTasks(values: Record<string, unknown> | undefined): Task[] {
  if (!values) return [];
  const raw = values.tasks;
  if (!Array.isArray(raw)) return [];
  return raw as Task[];
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface TasksContextValue {
  tasks: Task[];
  setTasks: (tasks: Task[]) => void;
  clearTasks: () => void;
}

const TasksContext = createContext<TasksContextValue | null>(null);

export function TasksProvider({ children }: { children: ReactNode }) {
  const [tasks, setTasksState] = useState<Task[]>([]);

  const setTasks = useCallback((newTasks: Task[]) => {
    setTasksState(newTasks);
  }, []);

  const clearTasks = useCallback(() => {
    setTasksState([]);
  }, []);

  return (
    <TasksContext.Provider value={{ tasks, setTasks, clearTasks }}>
      {children}
    </TasksContext.Provider>
  );
}

export function useTasks() {
  const ctx = useContext(TasksContext);
  if (!ctx) throw new Error('useTasks must be used within a TasksProvider');
  return ctx;
}
